import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  SIDEBAR_COLOUR_STORAGE_KEY,
  normaliseSidebarHex,
  applySidebarColour,
  readThemeSidebarDefault,
  loadSidebarSwatches,
  saveSidebarSwatches,
  normaliseSidebarSwatches,
  DEFAULT_SIDEBAR_SWATCHES,
} from '../utils/sidebarColour';

const THEME_STORAGE_KEY = 'theme';
const COLOR_THEME_STORAGE_KEY = 'colorTheme';

export const THEMES = Object.freeze({ light: 'light', dark: 'dark' });
export const COLOR_THEMES = Object.freeze({
  ocean: 'ocean',
  slate: 'slate',
  paper: 'paper',
  mist: 'mist',
  midnight: 'midnight',
  blush: 'blush',
  amber: 'amber',
  bloom: 'bloom',
  win95: 'win95',
});

const LEGACY_COLOR_THEMES = Object.freeze({
  gray: COLOR_THEMES.ocean,
  jade: COLOR_THEMES.ocean,
  hematite: COLOR_THEMES.slate,
  neutral: COLOR_THEMES.slate,
  stone: COLOR_THEMES.slate,
  zinc: COLOR_THEMES.slate,
  forest: COLOR_THEMES.ocean,
  candy: COLOR_THEMES.blush,
  storm: COLOR_THEMES.midnight,
  jungle: COLOR_THEMES.ocean,
  cherry: COLOR_THEMES.blush,
  violet: COLOR_THEMES.bloom,
  sunset: COLOR_THEMES.amber,
  teal: COLOR_THEMES.ocean,
  ink: COLOR_THEMES.slate,
});

function normalizeColorTheme(value) {
  if (Object.values(COLOR_THEMES).includes(value)) return value;
  if (LEGACY_COLOR_THEMES[value]) return LEGACY_COLOR_THEMES[value];
  return COLOR_THEMES.ocean;
}

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(THEMES.light);
  const [colorTheme, setColorThemeState] = useState(COLOR_THEMES.ocean);
  // Custom sidebar + surround colour (#rrggbb) or null to follow the theme.
  // Independent of light/dark and the colour theme: those only paint the main panel.
  const [sidebarColour, setSidebarColourState] = useState(null);
  // The colour the current theme paints the sidebar by itself (#rrggbb); shown
  // as the "Theme default" swatch and re-read whenever the theme changes.
  const [themeSidebarDefault, setThemeSidebarDefault] = useState(null);
  // The user's own ten swatch colours (built-ins until edited), per browser.
  const [sidebarSwatches, setSidebarSwatchesState] = useState(() => [...DEFAULT_SIDEBAR_SWATCHES]);

  const applyTheme = (mode, color) => {
    const root = document.documentElement;
    root.classList.remove('dark');
    if (mode === THEMES.dark) {
      root.classList.add('dark');
    }
    root.setAttribute('data-color-theme', color);
  };

  useEffect(() => {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY);
    const savedColor = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    const savedSidebar = normaliseSidebarHex(localStorage.getItem(SIDEBAR_COLOUR_STORAGE_KEY));
    const mode = savedMode === THEMES.dark ? THEMES.dark : THEMES.light;
    const color = normalizeColorTheme(savedColor);
    setThemeState(mode);
    setColorThemeState(color);
    setSidebarColourState(savedSidebar);
    setSidebarSwatchesState(loadSidebarSwatches(localStorage));
    applyTheme(mode, color);
    applySidebarColour(document.documentElement, savedSidebar);
    if (savedColor !== color) {
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, color);
    }
  }, []);

  useEffect(() => {
    setThemeSidebarDefault(readThemeSidebarDefault(document.documentElement));
  }, [theme, colorTheme]);

  const setTheme = (value) => {
    const next = value === THEMES.dark ? THEMES.dark : THEMES.light;
    setThemeState(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
    applyTheme(next, colorTheme);
  };

  const setColorTheme = (value) => {
    const next = normalizeColorTheme(value);
    setColorThemeState(next);
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, next);
    applyTheme(theme, next);
  };

  /** Set a custom sidebar colour (`#rrggbb`) or pass null to match the theme again. */
  const setSidebarColour = (value) => {
    const next = normaliseSidebarHex(value);
    setSidebarColourState(next);
    if (next) localStorage.setItem(SIDEBAR_COLOUR_STORAGE_KEY, next);
    else localStorage.removeItem(SIDEBAR_COLOUR_STORAGE_KEY);
    applySidebarColour(document.documentElement, next);
  };

  /** Replace one swatch slot (0–9) with a new colour. */
  const setSidebarSwatch = (index, value) => {
    const hex = normaliseSidebarHex(value);
    if (!hex || index < 0 || index >= DEFAULT_SIDEBAR_SWATCHES.length) return;
    setSidebarSwatchesState((prev) => {
      const next = normaliseSidebarSwatches(prev);
      next[index] = hex;
      saveSidebarSwatches(localStorage, next);
      return next;
    });
  };

  /** Restore the built-in swatch set. */
  const resetSidebarSwatches = () => {
    const next = [...DEFAULT_SIDEBAR_SWATCHES];
    setSidebarSwatchesState(next);
    saveSidebarSwatches(localStorage, next);
  };

  const isDark = theme === THEMES.dark;

  return (
    <ThemeContext.Provider
      value={{
        theme,
        colorTheme,
        isDark,
        setTheme,
        setColorTheme,
        sidebarColour,
        setSidebarColour,
        themeSidebarDefault,
        sidebarSwatches,
        setSidebarSwatch,
        resetSidebarSwatches,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
